/**
 * WebSocket 客户端 Composable
 * 用于管理 WebSocket 连接和消息接收
 */
import { ref, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { ElMessage } from 'element-plus'
import logger from '@/utils/logger'

interface WebSocketMessage {
  type: string
  // 修复: P0 - 使用具体类型 NotificationData 替代 any
  data: unknown
}

interface NotificationData {
  id: number
  title: string
  content: string
  notificationType: string
  priority: number
  relatedType?: string
  relatedId?: number
  actionType?: string
  actionUrl?: string
  createTime: string
}

interface WebSocketOptions {
  onNotification?: (notification: NotificationData) => void
  onConnected?: () => void
  onDisconnected?: () => void
  onError?: (error: Event) => void
}

const ws = ref<WebSocket | null>(null)
const isConnected = ref(false)
const reconnectTimer = ref<number | null>(null)
const heartbeatTimer = ref<number | null>(null)
const reconnectAttempts = ref(0)
const maxReconnectAttempts = 5
const reconnectDelay = 3000 // 3秒

/**
 * 连接状态锁，防止并发调用 connect() 导致竞态条件
 * 修复: P0 - 添加连接锁保护单例 WebSocket 连接
 */
const isConnecting = ref(false)

/**
 * 页面级事件（bfcache / 可见性）是否已注册，单例只注册一次
 */
let pageEventsRegistered = false

/**
 * 页面级事件回调的重连动作，由 useWebSocket() 每次调用时刷新。
 * connect/disconnect 定义在 useWebSocket() 内部，模块级回调无法直接引用，
 * 通过此间接引用桥接（修复 cc23861e 引入的 ReferenceError 隐患）。
 */
let pageEventReconnect: { connect: () => void; disconnect: () => void } | null = null

/**
 * 注册页面级事件，处理浏览器 Back-Forward Cache 导致的 WebSocket 失效
 * 修复: 浏览器将页面存入 bfcache 时旧 WebSocket 会被冻结/杀掉，恢复页面后需主动重建连接，
 *       否则实时通知功能中断；同时页面切回前台（visibilitychange）时若未连接也需重连
 */
function registerPageEvents() {
  if (pageEventsRegistered) return
  pageEventsRegistered = true

  // 页面从前进/后退缓存恢复：persisted=true 表示来自 bfcache 而非全新加载
  window.addEventListener('pageshow', (e: PageTransitionEvent) => {
    if (e.persisted && pageEventReconnect) {
      logger.debug('WebSocket: 页面从 bfcache 恢复，强制重建连接')
      reconnectAttempts.value = 0
      pageEventReconnect.disconnect()
      window.setTimeout(() => pageEventReconnect?.connect(), 100)
    }
  })

  // 页面重新可见且未连接时尝试重连（如切回标签页、bfcache 恢复后）
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && pageEventReconnect) {
      if (!isConnected.value && !isConnecting.value) {
        logger.debug('WebSocket: 页面可见且未连接，尝试重连')
        reconnectAttempts.value = 0
        pageEventReconnect.connect()
      }
    }
  })
}

/**
 * WebSocket 连接管理
 */
export function useWebSocket(options?: WebSocketOptions) {
  const authStore = useAuthStore()

  // 刷新页面级事件回调的重连动作（connect/disconnect 定义在本函数内部）
  pageEventReconnect = { connect, disconnect }
  // 注册 bfcache / 可见性重连（单例只注册一次）
  registerPageEvents()

  /**
   * 建立 WebSocket 连接
   * 修复: P0 - 添加连接状态锁，防止并发调用导致竞态条件
   */
  function connect() {
    if (!authStore.user?.id) {
      logger.debug('WebSocket: 用户未登录，跳过连接')
      return
    }

    // 连接状态锁保护：如果正在连接中，跳过本次调用
    if (isConnecting.value) {
      logger.debug('WebSocket: 正在连接中，跳过重复调用')
      return
    }

    // 已连接状态检查
    if (ws.value?.readyState === WebSocket.OPEN) {
      logger.debug('WebSocket: 已连接，跳过重复连接')
      return
    }

    // 设置连接锁
    isConnecting.value = true

    // 构建 WebSocket URL
    // 使用当前页面的 host 和 protocol，自动适配开发/生产环境
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    const wsUrl = `${protocol}//${host}/ws/notification?userId=${authStore.user.id}`

    logger.debug('WebSocket: 正在连接...', wsUrl)

    try {
      ws.value = new WebSocket(wsUrl)

      // 连接打开
      ws.value.onopen = () => {
        logger.debug('WebSocket: 连接成功')
        isConnected.value = true
        isConnecting.value = false // 释放连接锁
        reconnectAttempts.value = 0

        // 启动心跳
        startHeartbeat()

        // 触发回调
        options?.onConnected?.()
      }

      // 接收消息
      ws.value.onmessage = (event) => {
        const data = event.data

        // 心跳响应，直接跳过JSON解析
        if (data === 'pong' || data === 'ping') {
          logger.debug('WebSocket: 收到心跳响应', data)
          return
        }

        try {
          const message: WebSocketMessage = JSON.parse(data)
          logger.debug('WebSocket: 收到消息', message)

          handleMessage(message)
        } catch (error) {
          logger.error('WebSocket: 解析消息失败', error)
        }
      }

      // 连接关闭
      ws.value.onclose = (event) => {
        logger.debug('WebSocket: 连接关闭', event.code, event.reason)
        isConnected.value = false
        isConnecting.value = false // 释放连接锁
        stopHeartbeat()

        // 触发回调
        options?.onDisconnected?.()

        // 尝试重连
        if (reconnectAttempts.value < maxReconnectAttempts) {
          reconnectAttempts.value++
          logger.debug(`WebSocket: ${reconnectDelay / 1000}秒后尝试重连 (${reconnectAttempts.value}/${maxReconnectAttempts})`)
          reconnectTimer.value = window.setTimeout(() => {
            connect()
          }, reconnectDelay)
        } else {
          // P2-3修复: 达到最大重连次数时提示用户
          const errorMsg = '网络连接失败，通知功能暂时不可用，请刷新页面重试'
          logger.error('WebSocket: ' + errorMsg)
          ElMessage.warning(errorMsg)
          isConnecting.value = false // 释放连接锁
        }
      }

      // 连接错误
      ws.value.onerror = (error) => {
        // 页面不可见（如进入 bfcache 被冻结）时的瞬时错误属浏览器正常行为，不打扰用户
        if (document.visibilityState !== 'visible') {
          logger.debug('WebSocket: 页面不可见期间连接错误（bfcache场景），忽略', error)
          return
        }
        logger.error('WebSocket: 连接错误', error)
        // P2-3修复: 连接错误时提示用户（仅在非重连期间）
        if (reconnectAttempts.value === 0) {
          ElMessage.error('网络连接异常，通知功能可能受到影响')
        }
        options?.onError?.(error)
      }
    } catch (error) {
      logger.error('WebSocket: 创建连接失败', error)
      isConnecting.value = false // 释放连接锁
    }
  }

  /**
   * 断开 WebSocket 连接
   */
  function disconnect() {
    logger.debug('WebSocket: 主动断开连接')
    stopHeartbeat()
    isConnecting.value = false // 释放连接锁

    if (reconnectTimer.value) {
      clearTimeout(reconnectTimer.value)
      reconnectTimer.value = null
    }

    if (ws.value) {
      ws.value.close()
      ws.value = null
    }

    isConnected.value = false
    reconnectAttempts.value = 0
  }

  /**
   * 处理收到的消息
   * 修复: P0 - 添加类型守卫验证消息数据结构
   */
  function handleMessage(message: WebSocketMessage) {
    switch (message.type) {
      case 'CONNECTED':
        logger.debug('WebSocket: 服务器确认连接')
        break

      case 'NEW_NOTIFICATION':
        logger.debug('WebSocket: 收到新通知', message.data)
        // 类型守卫：验证数据是否为 NotificationData 结构
        if (isNotificationData(message.data)) {
          options?.onNotification?.(message.data)
        } else {
          logger.warn('WebSocket: 收到的通知数据格式不正确', message.data)
        }
        break

      case 'pong':
        // 心跳响应，不需要处理
        break

      default:
        logger.warn('WebSocket: 未知消息类型', message.type)
    }
  }

  /**
   * 类型守卫：验证数据是否为 NotificationData
   * 修复: P0 - 使用类型守卫确保类型安全
   */
  function isNotificationData(data: unknown): data is NotificationData {
    if (typeof data !== 'object' || data === null) {
      return false
    }
    const notificationData = data as Record<string, unknown>
    return (
      typeof notificationData.id === 'number' &&
      typeof notificationData.title === 'string' &&
      typeof notificationData.content === 'string' &&
      typeof notificationData.notificationType === 'string' &&
      typeof notificationData.priority === 'number' &&
      typeof notificationData.createTime === 'string'
    )
  }

  /**
   * 启动心跳检测
   * P2-4修复: 心跳失败时触发重连机制
   */
  function startHeartbeat() {
    stopHeartbeat()

    heartbeatTimer.value = window.setInterval(() => {
      if (ws.value?.readyState === WebSocket.OPEN) {
        try {
          ws.value.send('ping')
        } catch (error) {
          // P2-4修复: 心跳发送失败，视为连接断开，触发重连
          logger.error('WebSocket: 心跳发送失败', error)
          isConnected.value = false
          // 关闭当前连接触发重连流程
          if (ws.value) {
            ws.value.close()
          }
        }
      } else if (ws.value?.readyState !== WebSocket.OPEN && isConnected.value) {
        // P2-4修复: 连接状态异常，触发重连
        logger.warn('WebSocket: 连接状态异常，准备重连')
        isConnected.value = false
        if (ws.value) {
          ws.value.close()
        }
      }
    }, 30000) // 每30秒发送一次心跳
  }

  /**
   * 停止心跳检测
   */
  function stopHeartbeat() {
    if (heartbeatTimer.value) {
      clearInterval(heartbeatTimer.value)
      heartbeatTimer.value = null
    }
  }

  /**
   * 发送消息
   */
  function send(data: string | object) {
    if (ws.value?.readyState === WebSocket.OPEN) {
      const message = typeof data === 'string' ? data : JSON.stringify(data)
      ws.value.send(message)
    } else {
      logger.debug('WebSocket: 未连接，无法发送消息')
    }
  }

  // 组件卸载时清理
  onUnmounted(() => {
    disconnect()
  })

  return {
    isConnected,
    connect,
    disconnect,
    send
  }
}
